import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { IGroup } from 'app/models/group.type';
import { GroupService } from 'app/modules/admin/services/group.service';
import { MatMenuModule } from '@angular/material/menu';
import { DrawerModule } from '@gci/components/drawer/drawer.module';
import { SharedModule } from 'app/modules/admin/shared.module';
import { BaseComponent } from '@gci/components/base-page/base-component';
import { TranslocoService } from '@jsverse/transloco';
import { addGroupType, GroupAddFormComponent } from '../group-add-form/group-add-form.component';
import { AuthService } from 'app/core/auth/auth.service';
import { DropdownMenuComponent } from '../../dropdown-menu/dropdown-menu.component';
import { SolutionService } from 'app/modules/admin/services/solution.service';
import { Solution } from 'app/models/solution.type';
import { take, takeUntil } from 'rxjs';

@Component({
    selector: 'app-group-list',
    templateUrl: './group-list.component.html',
    styleUrls: ['./group-list.component.scss'],
    standalone: true,
    imports: [
        SharedModule,
        DrawerModule,
        MatMenuModule,
        GroupAddFormComponent,
        DropdownMenuComponent,
    ],
    exportAs: 'group-list'
})
export class GroupListComponent extends BaseComponent implements OnInit, OnDestroy {

    // -----------------------------------------------------------------------------------------------------
    // @ Properties
    // -----------------------------------------------------------------------------------------------------

    // @ViewChild('scrollableContainer') scrollableContainer!: ElementRef;
    @ViewChild('scrollableGroupList') scrollableContainerNew!: ElementRef;
    public group: IGroup[] = [];
    public activeGroup: IGroup | null = null;
    public drawerAddOpened: boolean = false;
    public drawerAddType: addGroupType = 'create';

    public solutionList: Solution[] = [];

    public groupCardStyle(group: IGroup, parts: 'background' | 'icon' | 'text'): string {
        const active: boolean = this.activeGroup !== null && this.activeGroup.id === group.id;
        switch(parts) {
            case 'background':
                return active 
                ? `bg-primary-base border-primary-base` 
                : `border-dashboard-400`;
            case 'icon':
                return active
                ? `text-primary-50`
                : `text-dashboard-400 dark:text-dashboard-50`;
            case 'text':
                return active
                ? `text-primary-50`
                : `text-dashboard-500 dark:text-white`;
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Constructor
    // -----------------------------------------------------------------------------------------------------

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _groupService: GroupService,
        private _authService: AuthService,
        private _translocoService: TranslocoService,
        private _solutionService: SolutionService,
    ) {
        super("GroupListComponent", _translocoService)

        this._solutionService.allItemsNewFormat$
        .pipe(takeUntil(this.unsubscribeAll))
        .subscribe({
            next: res => {
                if(res) {
                    const {data} = res;
                    this.solutionList = data;
                }
            }
        })

        this._solutionService.getSolution({limit: 100}).pipe(take(1)).subscribe();
    }
    
    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle Hooks
    // -----------------------------------------------------------------------------------------------------

    override ngOnInit() {
        super.ngOnInit();

        this._groupService.allGroup$.subscribe(
            res => {
                this.group = res;
                this._changeDetectorRef.detectChanges();
            }
        )

        this._groupService.activeGroup$.subscribe(
            res => {
                this.activeGroup = res;
                if(res) {
                    // IDK why, but some pages can't run this function properly unless I set a timeout
                    setTimeout(() => {
                        this.scrollTo(res.id)
                    }, 100)
                }
            }
        );

        if (!this.activeGroup) {
            this._groupService.getGroup().subscribe();
        }

    }

    override ngOnDestroy(): void {
        super.ngOnDestroy()
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    get canCreateUser() {
        let user = this._authService.currentUser;
        return user && (user?.tenant_id && user?.tenant_code_access === 1);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public Methods
    // -----------------------------------------------------------------------------------------------------

    getSolutionName(solution: string): string {
        const findSolution = this.solutionList.find(el => el.key === solution);
        return findSolution ? findSolution.title : solution;
    }

    getIconSolution(group: IGroup): string {
        if(!group.description) {
            const findSolution = this.solutionList.find(el => el.key === group.solution);
            if(findSolution) return findSolution.description;
        }
        return group.description || '';
    }

    /**
     * Close the drawer
     */
    cancelAddDrawer() {
        this.drawerAddOpened = false;
        setTimeout(() => {
            this.drawerAddType = 'create';
        }, 500);
        
        this._changeDetectorRef.detectChanges();
    }



    chooseGroup(group: IGroup) {
        this._groupService.activeGroup = group.id;
    }

    private isHeld: boolean = false;
    private isMouseDown: boolean = false;
    private holdDelay = 300;

    private scrollInterval: any;
    private holdTimeout: any;

    startScroll(direction: 'left' | 'right') {
        this.isHeld = false;
        this.isMouseDown = true;

        this.holdTimeout = setTimeout(() => {
            this.isHeld = true;
            this.scrollInterval = setInterval(() => {
                direction === 'left' ? this.moveLeft() : this.moveRight();
            }, this.holdDelay)
        }, 300)
    }

    stopScroll(direction: 'left' | 'right') {
        if(!this.isMouseDown) return;

        clearTimeout(this.holdTimeout);
        clearInterval(this.scrollInterval);
        
        if(!this.isHeld) {
            direction === 'left' ? this.moveLeft() : this.moveRight();
        }

        this.isMouseDown = false;
    }

    cancelIfHolding(direction: 'left' | 'right') {
        if(this.isMouseDown) {
            this.stopScroll(direction)
        }
    }

    /**
     * Move the horizontal scroll some length to the right
     */
    moveRight() {
        const container = this.scrollableContainerNew.nativeElement;

        container.scrollBy({
            top: 0,
            left: 200,
            behavior: 'smooth'
        });
    }

    /**
     * Move the horizontal scroll some length to the left
     */
    moveLeft() {
        const container = this.scrollableContainerNew.nativeElement;

        container.scrollBy({
            top: 0,
            left: -200,
            behavior: 'smooth'
        });
    }

    /**
     * Move the position to the selected group
     * @param section the group id
     */
    scrollTo(section: string): void {
        const container = this.scrollableContainerNew.nativeElement as HTMLElement;
        const target = document.getElementById(section);

        if (container && target) {
            const containerRect = container.getBoundingClientRect();
            const targetRect = target.getBoundingClientRect();

            
            const offsetLeft = targetRect.left - containerRect.left + container.scrollLeft;
            
            container.scrollTo({
                left: offsetLeft,
                behavior: 'smooth'
            }); 
        }
    }
}